import { FC, useState } from 'react';
import { Form, Formik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

import AuthWrapper from '../../common/AuthWrapper';
import CustomInput from '../../common/CustomInput';
import CustomPassInput from '../../common/CustomPassInput';
import CustomButton from '../../common/CustomButton';

import { useLoginUserMutation } from '../../store/services/authApi';
import { useAppDispatch } from '../../hooks/redux';
import { setCredentials, setFirstLoginStep } from '../../store/slices/authSlice';
import { useIsMobile } from '../../hooks/useWindowWidth';

import styles from './Login.module.scss';

const Login: FC = () => {
  const [loginUser] = useLoginUserMutation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();

  const [resErrors, setResErrors] = useState<{ attr: string | null; code: string; detail: string }[] | null>(null);

  const initialValues = {
    email: '',
    password: '',
  };

  const handleSubmit = async (values: { email: string; password: string }) => {
    try {
      const result = await loginUser({
        email: values.email,
        password: values.password,
      }).unwrap();

      if ((result as any)?.user) {
        dispatch(setFirstLoginStep(false));
        sessionStorage.removeItem('pendingCodeVerification');
        sessionStorage.removeItem('postCodeVerificationRoute');
        sessionStorage.removeItem('pendingLoginPassword');
        dispatch(setCredentials(result));
        navigate('/profile');
      } else {
        dispatch(setFirstLoginStep(true));
        localStorage.setItem('userEmail', values.email);
        sessionStorage.setItem('pendingCodeVerification', 'sign-in');
        sessionStorage.setItem('postCodeVerificationRoute', 'profile');
        sessionStorage.setItem('pendingLoginPassword', values.password);
        navigate('/reset-password');
      }

      if (result.access) {
        localStorage.setItem('authToken', result.access);
      }
      setResErrors(null);
    } catch (error) {
      console.error('Error logging in:', error);
      setResErrors((error as any)?.data?.errors || null);
    }
  };

  const formikValidate = (values: { email: string; password: string }) => {
    const errors: { email?: string; password?: string } = {};
    if (!values.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = 'Invalid email address';
    }
    if (!values.password.trim()) {
      errors.password = "Password can't be empty";
    } else if (values.password.length < 6) {
      errors.password = 'Password address is too short';
    }
    return errors;
  };

  return (
    <AuthWrapper btnName="Sign Up">
      <Box className={styles.loginContent}>
        <Typography
          variant={`${isMobile ? 'h6Bold' : 'h5Bold'}`}
          className={styles.title}
        >
          Login
        </Typography>
        <Formik
          initialValues={initialValues}
          validate={formikValidate}
          onSubmit={handleSubmit}
          validateOnMount={false}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => {
            const isLoginDisabled =
              isSubmitting || !values.email.trim() || !values.password.trim() || Object.keys(errors).length > 0;

            return (
              <Form className={styles.loginForm}>
                <CustomInput
                  inputLabel="Email"
                  inputType="email"
                  inputName="email"
                  value={values.email}
                  error={touched.email ? (errors.email ?? '') : ''}
                  customClass={styles.customInput}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <CustomPassInput
                  label="Password"
                  name="password"
                  value={values.password}
                  error={touched.password ? (errors.password ?? '') : ''}
                  customClass={styles.customInput}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <Link
                  to="/forgot-password"
                  className={styles.forgotPassLink}
                >
                  <Typography variant="h11Semibold">Forget password?</Typography>
                </Link>
                <CustomButton
                  btnType="submit"
                  customClass={styles.customButton}
                  disabled={isLoginDisabled}
                >
                  Login
                </CustomButton>

                {resErrors &&
                  resErrors.map((error, index) => (
                    <Typography
                      key={index}
                      variant="h12"
                      sx={{ color: '#C8102E' }}
                    >
                      {error.detail}
                    </Typography>
                  ))}
              </Form>
            );
          }}
        </Formik>

        <Box className={styles.signUpQuestion}>
          <Typography
            variant="h11"
            className={styles.signUpQuestionText}
          >
            Don't have an account?
          </Typography>
          <Link
            to="/sign-up"
            className={styles.signUpLink}
          >
            <Typography variant="h11Semibold">Sign Up</Typography>
          </Link>
        </Box>
      </Box>
    </AuthWrapper>
  );
};

export default Login;
